import { TaskModifier, useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { ScanLineItemOptimized } from '@kaiizen/kai-scan-template-system';
import { useNavigate } from 'react-router';

import { ScanDataMutators, useScanEditingContextSelector } from '@/Client/State';
import { RowKebabMenu, RowKebabMenuItem } from '@/Client/Widgets';

/**
 * Where the line item's template lives in the template editor: its group's page, with the line
 * item's template selected. Undefined for a line item with no template trail to follow.
 */
const useLineItemTemplateUrl = (groupId: string, taskId: string, optionId: string | undefined, lineItem: ScanLineItemOptimized | undefined) =>
  useScanEditingContextSelector(({ scanRoot, scanTemplateId }) => {
    const group = scanRoot?.groupsById[groupId];
    const task = group?.tasksById[taskId];
    const option = optionId ? task?.optionsById[optionId] : undefined;
    if (!scanTemplateId || !group?.templateId || !task?.templateId || !option?.templateId || !lineItem?.templateId) return undefined;
    const selection = new URLSearchParams({
      groupId: group.templateId,
      taskId: task.templateId,
      optionId: option.templateId,
      lineItemId: lineItem.templateId,
    });
    return `/template/${scanTemplateId}/group/${group.templateId}?${selection}`;
  }, [groupId, taskId, optionId, lineItem?.templateId]);

/**
 * A line item row's kebab, in sections: a toggle per task modifier the org has enabled (while the
 * row is editable); for the testingTools flag, a jump to the line item's template, a fake catalog
 * price change and a fake out-of-stock toggle when the caller offers them; then Remove from scope when the caller offers it. Always in view in its
 * reserved space at the row's end; with nothing to offer it's absent.
 */
export const FocusedLineItemStatusMenu = ({
  label,
  lineItem,
  groupId,
  taskId,
  optionId,
  scanDataMutators,
  enabledTaskModifiers,
  isEditable,
  removeLineItem,
  fakeCatalogPriceChange,
  fakingOutOfStock,
  toggleFakeOutOfStock,
}: {
  /** The row's own name (the product's title, or the line item's) for the trigger's accessible name. */
  label: string;
  lineItem?: ScanLineItemOptimized;
  groupId: string;
  taskId: string;
  optionId?: string;
  scanDataMutators: ScanDataMutators;
  enabledTaskModifiers: TaskModifier[];
  isEditable: boolean;
  /** When provided, the menu offers a Remove entry that invokes it. */
  removeLineItem?: () => void;
  /** For the testingTools flag: when provided, the menu offers Fake price change, which invokes it. */
  fakeCatalogPriceChange?: () => void;
  /** For the testingTools flag: whether the row is pretending its product is out of stock. */
  fakingOutOfStock?: boolean;
  /** For the testingTools flag: when provided, the menu offers the Fake out of stock toggle, which invokes it. */
  toggleFakeOutOfStock?: () => void;
}) => {
  const { featureFlags } = useFeatureFlags();
  const navigate = useNavigate();
  const templateUrl = useLineItemTemplateUrl(groupId, taskId, optionId, lineItem);

  const tagItems: RowKebabMenuItem[] = isEditable && lineItem && optionId
    ? enabledTaskModifiers.map(({ lineItemTag, displayName }) => ({
      label: displayName,
      section: 'Modifiers',
      checked: lineItem.tags?.[lineItemTag] === true,
      onSelect: () => {
        const { [lineItemTag]: current, ...otherTags } = lineItem.tags ?? {};
        scanDataMutators.updateLineItem(groupId, taskId, optionId, lineItem.id, {
          tags: current ? otherTags : { ...otherTags, [lineItemTag]: true },
        });
      },
    }))
    : [];

  return (
    <RowKebabMenu
      label={label}
      alwaysVisible
      items={[
        ...tagItems,
        ...featureFlags.testingTools && templateUrl ? [{ label: 'Go to template', section: 'Testing tools', onSelect: () => navigate(templateUrl) }] : [],
        ...featureFlags.testingTools && fakeCatalogPriceChange
          ? [{ label: 'Fake price change', section: 'Testing tools', onSelect: fakeCatalogPriceChange }]
          : [],
        ...featureFlags.testingTools && toggleFakeOutOfStock
          ? [{ label: 'Fake out of stock', section: 'Testing tools', checked: !!fakingOutOfStock, onSelect: toggleFakeOutOfStock }]
          : [],
        ...removeLineItem ? [{ label: 'Remove from scope', destructive: true, onSelect: removeLineItem }] : [],
      ]}
    />
  );
};
